import { useEffect, useRef, useState } from "react";
import {
  WHEEL_ORDER,
  colorOf,
  wheelRotationFor,
} from "@ziolo/shared";

const CX = 100;
const CY = 100;
const R_OUTER = 90;
const R_INNER = 42;

function polar(cx: number, cy: number, r: number, angle: number): [number, number] {
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function wedgePath(index: number): string {
  const step = (2 * Math.PI) / WHEEL_ORDER.length;
  const start = -Math.PI / 2 + index * step;
  const end = start + step;
  const [x0, y0] = polar(CX, CY, R_OUTER, start);
  const [x1, y1] = polar(CX, CY, R_OUTER, end);
  const [x2, y2] = polar(CX, CY, R_INNER, end);
  const [x3, y3] = polar(CX, CY, R_INNER, start);
  return `M ${x0} ${y0} A ${R_OUTER} ${R_OUTER} 0 0 1 ${x1} ${y1} L ${x2} ${y2} A ${R_INNER} ${R_INNER} 0 0 0 ${x3} ${y3} Z`;
}

function labelPos(index: number): { x: number; y: number; rotate: number } {
  const step = (2 * Math.PI) / WHEEL_ORDER.length;
  const mid = -Math.PI / 2 + index * step + step / 2;
  const r = (R_OUTER + R_INNER) / 2 + 4;
  const [x, y] = polar(CX, CY, r, mid);
  return { x, y, rotate: (mid * 180) / Math.PI + 90 };
}

export function RuletkaWheel({
  winningNumber,
  spinning,
  spinMs,
  roundNumber,
  hubLabel,
}: {
  winningNumber: number | null;
  spinning: boolean;
  spinMs: number;
  roundNumber: number;
  hubLabel: string;
}) {
  const [rotation, setRotation] = useState(0);
  const [ballRot, setBallRot] = useState(0);
  const rotationRef = useRef(0);
  const ballRef = useRef(0);
  const lastRound = useRef(-1);

  useEffect(() => {
    if (winningNumber === null) return;
    if (spinning && lastRound.current !== roundNumber) {
      lastRound.current = roundNumber;
      const next = wheelRotationFor(winningNumber, 5, rotationRef.current);
      rotationRef.current = next;
      setRotation(next);
      const nextBall = Math.floor(ballRef.current / 360) * 360 - 7 * 360;
      ballRef.current = nextBall;
      setBallRot(nextBall);
      return;
    }
    if (!spinning && lastRound.current !== roundNumber) {
      const parked = wheelRotationFor(winningNumber, 0, 0);
      const normalized = ((parked % 360) + 360) % 360;
      rotationRef.current = normalized;
      setRotation(normalized);
      lastRound.current = roundNumber;
      ballRef.current = 0;
      setBallRot(0);
    }
  }, [winningNumber, spinning, roundNumber]);

  const color = winningNumber === null ? null : colorOf(winningNumber);

  return (
    <div className="rl-wheel">
      <div className="rl-wheel__pointer" aria-hidden="true" />
      <svg className="rl-wheel__svg" viewBox="0 0 200 200" role="img" aria-label="Koło ruletki">
        <circle cx={CX} cy={CY} r={99} className="rl-wheel__wood" />
        <circle cx={CX} cy={CY} r={94} className="rl-wheel__gold" />
        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: "100px 100px",
            transition: spinning ? `transform ${spinMs}ms cubic-bezier(0.12, 0.7, 0.16, 1)` : "none",
          }}
        >
          {WHEEL_ORDER.map((n, index) => {
            const pocket = colorOf(n);
            const label = labelPos(index);
            return (
              <g key={n}>
                <path d={wedgePath(index)} className={`rl-wheel__pocket rl-wheel__pocket--${pocket}`} />
                <text
                  x={label.x}
                  y={label.y}
                  className="rl-wheel__label"
                  transform={`rotate(${label.rotate} ${label.x} ${label.y})`}
                >
                  {n}
                </text>
              </g>
            );
          })}
        </g>
        <circle cx={CX} cy={CY} r={R_INNER - 1} className="rl-wheel__hub" />
      </svg>
      <div
        className="rl-wheel__orbit"
        style={{
          transform: `rotate(${ballRot}deg)`,
          transition: spinning ? `transform ${spinMs}ms cubic-bezier(0.08, 0.55, 0.2, 1)` : "none",
        }}
        aria-hidden="true"
      >
        <span className="rl-wheel__ball" />
      </div>
      <div className={`rl-wheel__readout${color && !spinning ? ` rl-wheel__readout--${color}` : ""}`}>
        {spinning ? (
          <span className="rl-wheel__mask">Koniec zakładów</span>
        ) : (
          <span className="rl-wheel__mask">{hubLabel}</span>
        )}
      </div>
    </div>
  );
}
